interface PermissionCacheEntry {
  permissions: string[];
  roles: string[];
  timestamp: number;
  establishmentId: string;
}

interface PermissionCheckCache {
  [key: string]: boolean | number | undefined;
  timestamp?: number;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const CHECK_CACHE_DURATION = 1 * 60 * 1000; // 1 minute for permission checks

class PermissionCache {
  private cache: Map<string, PermissionCacheEntry> = new Map();
  private checkCache: Map<string, PermissionCheckCache> = new Map();

  /**
   * Get cached permissions for a user and establishment
   */
  get(userId: string, establishmentId: string): PermissionCacheEntry | null {
    const key = `${userId}:${establishmentId}`;
    const entry = this.cache.get(key);

    if (!entry) {
      return null;
    }

    // Check if cache is expired
    if (Date.now() - entry.timestamp > CACHE_DURATION) {
      this.cache.delete(key);
      // Also clear related permission checks
      this.clearCheckCacheForKey(key);
      return null;
    }

    return entry;
  }

  /**
   * Set permissions in cache
   */
  set(userId: string, establishmentId: string, permissions: string[], roles: string[]): void {
    const key = `${userId}:${establishmentId}`;
    
    this.cache.set(key, {
      permissions,
      roles,
      timestamp: Date.now(),
      establishmentId
    });

    // Clear permission check cache when permissions are updated
    this.clearCheckCacheForKey(key);
  }

  /**
   * Clear cache for a specific user and establishment
   */
  clear(userId: string, establishmentId: string): void {
    const key = `${userId}:${establishmentId}`;
    this.cache.delete(key);
    this.clearCheckCacheForKey(key);
  }

  /**
   * Clear all cached data
   */
  clearAll(): void {
    this.cache.clear();
    this.checkCache.clear();
  }

  /**
   * Get cached permission check result
   */
  getPermissionCheck(userId: string, establishmentId: string, permission: string): boolean | null {
    const key = `${userId}:${establishmentId}:${permission}`;
    const entry = this.checkCache.get(key);

    if (!entry || !entry.timestamp) {
      return null;
    }

    // Check if cache is expired
    if (Date.now() - entry.timestamp > CHECK_CACHE_DURATION) {
      this.checkCache.delete(key);
      return null;
    }

    const result = entry[permission];
    return typeof result === 'boolean' ? result : false;
  }

  /**
   * Cache permission check result
   */
  setPermissionCheck(userId: string, establishmentId: string, permission: string, result: boolean): void {
    const key = `${userId}:${establishmentId}:${permission}`;
    
    this.checkCache.set(key, {
      [permission]: result,
      timestamp: Date.now()
    });
  }

  /**
   * Clear permission check cache for a user/establishment
   */
  private clearCheckCacheForKey(userEstablishmentKey: string): void {
    // Clear all permission checks for this user/establishment combination
    const keysToDelete: string[] = [];
    
    this.checkCache.forEach((_, key) => {
      if (key.startsWith(userEstablishmentKey + ':')) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach(key => this.checkCache.delete(key));
  }

  /**
   * Get cache statistics
   */
  getStats(): {
    permissionCacheSize: number;
    checkCacheSize: number;
    oldestEntry: number | null;
  } {
    let oldestTimestamp: number | null = null;

    this.cache.forEach(entry => {
      if (!oldestTimestamp || entry.timestamp < oldestTimestamp) {
        oldestTimestamp = entry.timestamp;
      }
    });

    return {
      permissionCacheSize: this.cache.size,
      checkCacheSize: this.checkCache.size,
      oldestEntry: oldestTimestamp
    };
  }

  /**
   * Clean up expired entries
   */
  cleanup(): void {
    const now = Date.now();

    // Clean permission cache
    const expiredKeys: string[] = [];
    this.cache.forEach((entry, key) => {
      if (now - entry.timestamp > CACHE_DURATION) {
        expiredKeys.push(key);
      }
    });
    expiredKeys.forEach(key => {
      this.cache.delete(key);
      this.clearCheckCacheForKey(key);
    });

    // Clean check cache
    const expiredCheckKeys: string[] = [];
    this.checkCache.forEach((entry, key) => {
      if (entry.timestamp && now - entry.timestamp > CHECK_CACHE_DURATION) {
        expiredCheckKeys.push(key);
      }
    });
    expiredCheckKeys.forEach(key => this.checkCache.delete(key));
  }
}

// Create singleton instance
export const permissionCache = new PermissionCache();

// Set up periodic cleanup
const isTestEnv =
  typeof process !== 'undefined' &&
  (process.env['NODE_ENV'] === 'test' || process.env['VITEST'] !== undefined);

if (typeof window !== 'undefined' && !isTestEnv) {
  setInterval(() => {
    permissionCache.cleanup();
  }, 60 * 1000); // Run cleanup every minute
}

// Helper functions for easier usage
export const getCachedPermissions = (userId: string, establishmentId: string) => 
  permissionCache.get(userId, establishmentId);

export const setCachedPermissions = (
  userId: string, 
  establishmentId: string, 
  permissions: string[], 
  roles: string[]
) => permissionCache.set(userId, establishmentId, permissions, roles);

export const clearPermissionCache = (userId: string, establishmentId: string) => 
  permissionCache.clear(userId, establishmentId);

export const clearAllPermissionCache = () => permissionCache.clearAll();
